/**
 * Per-practice-group approval flows.
 *
 * A practice group described its approvals as a SEQUENCE — "General Counsel, then the Practice
 * Group Leader, then the Responsible Partner" — and the trigger table above cannot express that.
 * Its `order` is one global number per step, so no condition can make two steps run in one order
 * for one group and the reverse for another.
 *
 * So a group's flow is a list, and the list is the order. Where a group has one it IS the flow:
 * the conditional gates above are not consulted for that group's matters. That trade is stated on
 * screen rather than left for somebody to discover, because it is the whole behaviour of the
 * feature — if General Counsel should see a matter, General Counsel has to be in the list.
 *
 * A group with no list keeps the firm-wide flow exactly, which is what makes this safe to roll
 * out one group at a time.
 */

import { useState } from 'react';
import { sortPeople } from '../../lib/sortPeople';
import { dedupePeople, personLabel } from '../../lib/people';

const field = 'w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm';

const RECIPIENTS = [
  ['managing_partner', 'Managing Partner'],
  ['general_counsel', 'General Counsel'],
  ['practice_group_leader', 'Practice Group Leader'],
  ['responsible_partner', 'Responsible Partner (named on the matter)'],
  ['specific_user', 'Someone else'],
  ['team_leader', 'Team Leader'],
  ['intake', 'Intake (anyone with the Intake role)'],
];

const CC_KINDS = [
  ['managing_partner', 'Managing Partner'],
  ['general_counsel', 'General Counsel'],
  ['practice_group_leader', 'Practice Group Leader'],
  ['responsible_partner', 'Responsible Partner'],
  ['team_leader', 'Team leader'],
  ['user', 'Someone else'],
  ['email', 'Other email address'],
];

const STEP_KINDS = [
  ['approval', 'Approval — approve, reject, or request more information'],
  ['notify', 'Notify — a one-way email; nothing to decide'],
];

const blankStep = () => ({
  label: 'New step',
  kind: 'approval',
  resolver: 'practice_group_leader',
  satisfyMode: 'all',
  reasonTemplate: '',
  cc: [],
});

export default function GroupFlowEditor({ flow, setFlow, practiceGroups, roster }) {
  const groups = (practiceGroups || []).filter((g) => g.isActive !== false);
  const [groupId, setGroupId] = useState(groups[0]?.id || '');
  const groupFlows = flow.groupFlows || {};
  const current = groupFlows[groupId] || null;
  const steps = Array.isArray(current?.steps) ? current.steps : [];

  const setGroupFlow = (next) =>
    setFlow({ ...flow, groupFlows: { ...groupFlows, [groupId]: next } });

  const patchSteps = (nextSteps) => setGroupFlow({ ...(current || {}), steps: nextSteps });
  const patchStep = (index, patch) =>
    patchSteps(steps.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  /** Up and down rather than drag: this list is three or four entries, and order is the point. */
  const move = (index, by) => {
    const to = index + by;
    if (to < 0 || to >= steps.length) return;
    const next = [...steps];
    [next[index], next[to]] = [next[to], next[index]];
    patchSteps(next);
  };

  const patchCc = (stepIndex, ccIndex, patch) => {
    const list = Array.isArray(steps[stepIndex].cc) ? steps[stepIndex].cc : [];
    patchStep(stepIndex, {
      cc: list.map((entry, i) =>
        i === ccIndex ? { ...('kind' in patch ? { kind: entry.kind } : entry), ...patch } : entry
      ),
    });
  };

  const people = sortPeople(dedupePeople(roster || []));

  return (
    <section className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        Practice group flows
      </h2>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        A practice group can have its own sequence of approvals. Where it does, that list{' '}
        <strong>is</strong> the flow for its matters — the conditional steps above are not applied
        as well, so anyone who must see the matter has to be on the list. Groups without a list
        keep the firm-wide flow exactly as it is.
      </p>

      <label className="mt-3 block text-xs text-slate-600 dark:text-slate-300">
        Practice group
        <select className={`${field} mt-1`} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name || g.id}
              {Array.isArray(groupFlows[g.id]?.steps) && groupFlows[g.id].steps.length
                ? ` — own flow (${groupFlows[g.id].steps.length} steps)`
                : ' — firm-wide flow'}
            </option>
          ))}
        </select>
      </label>

      {!groupId ? null : !current || !steps.length ? (
        <div className="mt-3 rounded border border-dashed border-slate-300 p-4 text-center dark:border-slate-600">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            This group uses the firm-wide flow.
          </p>
          <button
            type="button"
            onClick={() => setGroupFlow({ enabled: true, steps: [blankStep()] })}
            className="mt-2 rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Give it its own flow
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={current.enabled !== false}
              onChange={(e) => setGroupFlow({ ...current, enabled: e.target.checked })}
            />
            Use this flow. Unticked, the group falls back to the firm-wide flow without losing what
            is written here.
          </label>

          {steps.map((step, index) => (
            <div key={index} className="rounded border border-slate-200 p-3 dark:border-slate-700">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Step {index + 1}
                </span>
                <div className="flex items-center gap-2 text-xs">
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0} className="disabled:opacity-30">
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === steps.length - 1}
                    className="disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => patchSteps(steps.filter((_, i) => i !== index))}
                    className="text-red-600 hover:underline dark:text-red-400"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <label className="text-xs text-slate-600 dark:text-slate-300">
                  Name of this step
                  <input
                    className={`${field} mt-1`}
                    value={step.label || ''}
                    onChange={(e) => patchStep(index, { label: e.target.value })}
                  />
                </label>

                <label className="text-xs text-slate-600 dark:text-slate-300">
                  Goes to
                  <select
                    className={`${field} mt-1`}
                    value={step.resolver || 'practice_group_leader'}
                    onChange={(e) => patchStep(index, { resolver: e.target.value })}
                  >
                    {RECIPIENTS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                {step.resolver === 'specific_user' && (
                  <label className="text-xs text-slate-600 dark:text-slate-300">
                    Person
                    <select
                      className={`${field} mt-1`}
                      value={step.approverUid || ''}
                      onChange={(e) => patchStep(index, { approverUid: e.target.value })}
                    >
                      <option value="">Select…</option>
                      {people.map((u) => (
                        <option key={u.id} value={u.id}>
                          {personLabel(u)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="text-xs text-slate-600 dark:text-slate-300">
                  What they do
                  <select
                    className={`${field} mt-1`}
                    value={step.kind || 'approval'}
                    onChange={(e) => patchStep(index, { kind: e.target.value })}
                  >
                    {STEP_KINDS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
                  Also copy on this email
                  <div className="mt-1 space-y-2">
                    {(Array.isArray(step.cc) ? step.cc : []).map((entry, ccIndex) => (
                      <div key={ccIndex} className="flex flex-wrap items-center gap-2">
                        <select
                          className={`${field} w-auto`}
                          value={entry.kind || 'email'}
                          onChange={(e) => patchCc(index, ccIndex, { kind: e.target.value })}
                        >
                          {CC_KINDS.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                        {entry.kind === 'user' && (
                          <select
                            className={`${field} w-auto`}
                            value={entry.uid || ''}
                            onChange={(e) => patchCc(index, ccIndex, { uid: e.target.value })}
                          >
                            <option value="">Select…</option>
                            {people.map((u) => (
                              <option key={u.id} value={u.id}>
                                {personLabel(u)}
                              </option>
                            ))}
                          </select>
                        )}
                        {entry.kind === 'email' && (
                          <input
                            className={`${field} w-auto`}
                            placeholder="records@aalrr.com"
                            value={entry.email || ''}
                            onChange={(e) => patchCc(index, ccIndex, { email: e.target.value })}
                          />
                        )}
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:underline dark:text-red-400"
                          onClick={() =>
                            patchStep(index, { cc: (step.cc || []).filter((_, i) => i !== ccIndex) })
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="rounded border border-dashed border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                      onClick={() => patchStep(index, { cc: [...(step.cc || []), { kind: 'email', email: '' }] })}
                    >
                      + Add a copy
                    </button>
                  </div>
                </div>

                <label className="text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
                  Message shown to them
                  <textarea
                    rows={2}
                    className={`${field} mt-1`}
                    value={step.reasonTemplate || ''}
                    onChange={(e) => patchStep(index, { reasonTemplate: e.target.value })}
                  />
                </label>
              </div>
            </div>
          ))}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => patchSteps([...steps, blankStep()])}
              className="rounded border border-dashed border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              + Add a step
            </button>
            <button
              type="button"
              onClick={() => {
                const next = { ...groupFlows };
                delete next[groupId];
                setFlow({ ...flow, groupFlows: next });
              }}
              className="text-xs text-red-600 hover:underline dark:text-red-400"
            >
              Delete this group's flow and go back to the firm-wide one
            </button>
          </div>

          {/* The order is the whole feature, so it is restated as a sentence rather than left to
              be read off a column of cards. */}
          <p className="rounded bg-slate-50 p-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <strong>This group's matters go to:</strong>{' '}
            {steps.map((s) => s.label || 'Unnamed step').join(' → ') || 'nobody yet'}
          </p>
        </div>
      )}
    </section>
  );
}
